/**
 * CommitmentScreen (Screen 7)
 * 
 * "How much time can you realistically commit daily?"
 * Refined for perfect vertical balance: larger cards and optimized gaps for a premium feel.
 */
import React from 'react';
import { useOnboardingViewport } from '../shared/useViewport';
import { useOnboarding } from '../../../context/OnboardingContext';

const TimerIcon = ({ color }) => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const CommitmentCard = ({ option, selected, onSelect, vp }) => (
  <div
    onClick={onSelect}
    style={{
      background: 'white',
      padding: '12px 20px',
      borderRadius: '24px',
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      cursor: 'pointer',
      border: `2px solid ${selected ? option.color : 'rgba(0,0,0,0.03)'}`,
      boxShadow: selected ? `0 4px 15px ${option.color}10` : '0 2px 8px rgba(0,0,0,0.02)',
      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
      position: 'relative',
      height: `${vp.sp(84, 92, 100)}px`, // Increased card size
    }}
  >
    {/* Icon Area */}
    <div style={{
      width: `${vp.sp(56, 62, 66)}px`,
      height: `${vp.sp(56, 62, 66)}px`,
      borderRadius: '16px',
      background: `${option.color}10`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0
    }}>
      <TimerIcon color={option.color} />
    </div>

    {/* Text Area */}
    <div style={{ flex: 1, textAlign: 'left', minWidth: 0 }}>
      <div style={{ fontSize: '19px', fontWeight: '900', color: '#0F172A', lineHeight: '1.2' }}>{option.title}</div>
      <div style={{ 
        fontSize: '12.5px', 
        color: '#64748B', 
        lineHeight: '1.2', 
        marginTop: '3px', 
        fontWeight: '600',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
      }}>
        {option.subtitle}
      </div>
    </div>

    {/* Radio Indicator */}
    <div style={{
      width: '30px',
      height: '30px',
      borderRadius: '50%',
      border: `2.5px solid ${selected ? option.color : '#E2E8F0'}`,
      background: 'transparent',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.2s ease',
      flexShrink: 0
    }}>
      {selected && (
        <div style={{
          width: '16px',
          height: '16px',
          borderRadius: '50%',
          background: option.color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
      )}
    </div>
  </div>
);

export const CommitmentScreen = ({ onNext }) => {
  const vp = useOnboardingViewport();
  const { commitment, updateOnboarding } = useOnboarding();
  const [selectedOption, setSelectedOption] = React.useState(commitment || '5m');

  const handleSelect = (id) => {
    setSelectedOption(id);
    updateOnboarding({ commitment: id });
  };

  const options = [
    {
      id: '5m',
      title: '5 minutes',
      subtitle: 'A great way to build the habit',
      color: '#14B8A6'
    },
    {
      id: '10m',
      title: '10 minutes',
      subtitle: 'Balanced and effective',
      color: '#F87171'
    },
    {
      id: '15m',
      title: '15 minutes',
      subtitle: 'More practice, faster progress',
      color: '#FBBF24'
    },
    {
      id: '20m',
      title: '20+ minutes',
      subtitle: 'Maximum learning every day',
      color: '#A78BFA'
    }
  ];

  const primaryCTAStyle = {
    width: "100%",
    height: `${vp.spacing.ctaHeight}px`,
    background: "#14B8A6",
    borderRadius: `${vp.spacing.ctaBorderRadius}px`,
    border: "none",
    boxShadow: "0 8px 16px rgba(20, 184, 166, 0.2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    color: "white",
    fontSize: `${vp.spacing.ctaFontSize}px`,
    fontWeight: "900",
    cursor: "pointer",
    transition: "transform 0.2s",
  };

  const isDesktop = vp.isTablet;

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      width: '100%',
      maxWidth: isDesktop ? '480px' : '100%',
      margin: isDesktop ? '0 auto' : undefined,
      animation: 'fadeIn 0.6s ease-out',
      position: 'relative',
      background: 'transparent'
    }}>
      {/* 1. Headline Area */}
      <div style={{ textAlign: 'center', marginTop: '-15px' }}>
        <h2 style={{
          fontSize: vp.headlineFontSize,
          fontWeight: '900',
          color: '#0F172A',
          lineHeight: '1.1',
          margin: '0',
          letterSpacing: '-1.5px'
        }}>
          How much time can you<br />
          realistically <span style={{ color: '#14B8A6' }}>commit daily?</span>
        </h2>
      </div>

      {/* 2. Selection Cards — Optimized for presence */}
      <div style={{ 
        flex: 1,
        display: 'flex', 
        flexDirection: 'column', 
        gap: '18px', 
        marginTop: '32px',
        justifyContent: 'center'
      }}>
        {options.map(option => (
          <CommitmentCard
            key={option.id}
            option={option}
            selected={selectedOption === option.id}
            onSelect={() => handleSelect(option.id)}
            vp={vp}
          />
        ))}
      </div>

      {/* 3. Bottom CTA — Pushed firmly down */}
      <div style={{
        paddingTop: '10px',
        zIndex: 10
      }}>
        <button
          onClick={onNext}
          style={primaryCTAStyle}
        >
          Build My Plan
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default CommitmentScreen;
